import React, { useState, useEffect } from "react";
import { Link, Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  LogOut,
  ChevronRight,
  PlayCircle,
  CheckCircle,
  Circle,
  Menu,
  Settings,
  BookOpen,
  GraduationCap,
  BarChart2,
} from "lucide-react";
import { clearCredentials } from "../slices/authSlice";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { useLogoutMutation } from "../services/auth";
import { useGetStudentCoursesQuery } from "../services/courses";

function cn(...cls: Array<string | false | null | undefined>) {
  return cls.filter(Boolean).join(" ");
}

/* ── Progress ring ───────────────────────────────────────────── */
const ProgressRing: React.FC<{ pct: number; size?: number }> = ({
  pct,
  size = 36,
}) => {
  const r = (size - 4) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (pct / 100) * circ;
  return (
    <svg width={size} height={size} className="rotate-[-90deg]">
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        stroke="#e2e8f0"
        strokeWidth={3}
        fill="none"
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        stroke="#14b8a6"
        strokeWidth={3}
        fill="none"
        strokeDasharray={circ}
        strokeDashoffset={offset}
        strokeLinecap="round"
        style={{ transition: "stroke-dashoffset 0.5s ease" }}
      />
    </svg>
  );
};

/* ── Lesson row ──────────────────────────────────────────────── */
const LessonRow: React.FC<{
  lesson: any;
  active: boolean;
  onClick: () => void;
  index: number;
}> = ({ lesson, active, onClick, index }) => (
  <button
    onClick={onClick}
    className={cn(
      "group w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left text-sm transition-all duration-150",
      active
        ? "bg-teal-500 text-white shadow-sm shadow-teal-200"
        : "text-slate-600 hover:bg-slate-100 hover:text-slate-900",
    )}
  >
    {/* status icon */}
    <div className="shrink-0">
      {lesson.completed ? (
        <CheckCircle
          size={15}
          className={active ? "text-white" : "text-teal-500"}
        />
      ) : (
        <Circle
          size={15}
          className={active ? "text-white/70" : "text-slate-300"}
        />
      )}
    </div>

    {/* title */}
    <span className="flex-1 truncate leading-snug">{lesson.title}</span>

    {/* play icon on hover */}
    <PlayCircle
      size={14}
      className={cn(
        "shrink-0 transition-opacity",
        active
          ? "opacity-100 text-white"
          : "opacity-0 group-hover:opacity-100 text-slate-400",
      )}
    />
  </button>
);

/* ── Module accordion ────────────────────────────────────────── */
const ModuleAccordion: React.FC<{
  module: any;
  defaultOpen?: boolean;
  currentLessonId: string;
  onLesson: (id: string) => void;
}> = ({ module, defaultOpen, currentLessonId, onLesson }) => {
  const [open, setOpen] = useState(!!defaultOpen);
  const lessons: any[] = module.lessons || [];
  const completed = lessons.filter((l) => l.completed).length;
  const pct = lessons.length
    ? Math.round((completed / lessons.length) * 100)
    : 0;

  return (
    <div className="rounded-xl border border-slate-100 overflow-hidden">
      {/* module header */}
      <button
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "w-full flex items-center gap-3 px-3 py-3 text-left transition-colors",
          open ? "bg-slate-50" : "bg-white hover:bg-slate-50",
        )}
      >
        {/* progress ring */}
        <div className="shrink-0 relative">
          <ProgressRing pct={pct} size={32} />
          <span className="absolute inset-0 flex items-center justify-center text-[9px] font-bold text-teal-600">
            {pct}%
          </span>
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-slate-800 truncate leading-snug">
            {module.title}
          </p>
          <p className="text-[10px] text-slate-400 mt-0.5">
            {completed}/{lessons.length} lessons
          </p>
        </div>

        <ChevronRight
          size={14}
          className={cn(
            "shrink-0 text-slate-400 transition-transform duration-200",
            open && "rotate-90",
          )}
        />
      </button>

      {/* lessons list */}
      {open && (
        <div className="px-2 pb-2 pt-1 space-y-0.5 bg-white border-t border-slate-100">
          {lessons.map((lesson: any, i: number) => (
            <LessonRow
              key={lesson._id}
              lesson={lesson}
              index={i}
              active={currentLessonId === lesson._id}
              onClick={() => onLesson(lesson._id)}
            />
          ))}
          {lessons.length === 0 && (
            <p className="px-3 py-2 text-xs text-slate-400 italic">
              No lessons yet.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

/* ── Course accordion ────────────────────────────────────────── */
const CourseAccordion: React.FC<{
  course: any;
  currentLessonId: string;
  onLesson: (id: string) => void;
}> = ({ course, currentLessonId, onLesson }) => {
  const [open, setOpen] = useState(false);
  const modules: any[] = course.modules || [];

  // auto-open if a lesson in this course is active
  const hasActive = modules.some((m) =>
    (m.lessons || []).some((l: any) => l._id === currentLessonId),
  );

  useEffect(() => {
    if (hasActive) setOpen(true);
  }, [hasActive]);

  const allLessons = modules.flatMap((m) => m.lessons || []);
  const completed = allLessons.filter((l) => l.completed).length;
  const pct = allLessons.length
    ? Math.round((completed / allLessons.length) * 100)
    : 0;

  return (
    <div
      className={cn(
        "rounded-2xl border transition-all duration-150",
        open
          ? "border-teal-200 shadow-sm"
          : "border-slate-200 hover:border-slate-300",
      )}
    >
      {/* course header */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 px-4 py-3.5 text-left rounded-2xl"
      >
        <div
          className={cn(
            "shrink-0 w-9 h-9 rounded-xl flex items-center justify-center transition-colors",
            open ? "bg-teal-500 text-white" : "bg-teal-50 text-teal-600",
          )}
        >
          <BookOpen size={16} />
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-900 truncate leading-snug">
            {course.title}
          </p>
          <div className="flex items-center gap-2 mt-1">
            {/* progress bar */}
            <div className="flex-1 h-1 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-teal-400 rounded-full transition-all duration-500"
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className="text-[10px] font-semibold text-slate-400 shrink-0">
              {pct}%
            </span>
          </div>
        </div>

        <ChevronRight
          size={15}
          className={cn(
            "shrink-0 text-slate-400 transition-transform duration-200",
            open && "rotate-90",
          )}
        />
      </button>

      {/* modules */}
      {open && (
        <div className="px-3 pb-3 space-y-2 border-t border-slate-100 pt-3">
          {modules.map((mod: any, i: number) => (
            <ModuleAccordion
              key={mod._id}
              module={mod}
              defaultOpen={i === 0}
              currentLessonId={currentLessonId}
              onLesson={onLesson}
            />
          ))}
          {modules.length === 0 && (
            <p className="text-xs text-slate-400 italic px-2 py-1">
              No modules in this course yet.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

/* ── Main Layout ─────────────────────────────────────────────── */
const StudentLayout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const [logoutApi] = useLogoutMutation();

  const auth = useAppSelector((s) => s.auth);
  const { data: courses = [], isLoading: coursesLoading } =
    useGetStudentCoursesQuery(undefined);

  // derive current lesson id from url
  const lessonMatch = location.pathname.match(/\/lesson\/([^/]+)/);
  const currentLessonId = lessonMatch ? lessonMatch[1] : "";

  // page title from location
  const pageTitle = (() => {
    if (location.pathname === "/dashboard") return "Overview";
    if (location.pathname.includes("/lesson/")) return "Lesson";
    if (location.pathname.includes("/settings")) return "Settings";
    return "Dashboard";
  })();

  async function handleLogout() {
    try {
      await logoutApi(undefined).unwrap();
    } catch {}
    dispatch(clearCredentials());
    navigate("/");
  }

  function goToLesson(lessonId: string) {
    navigate(`/dashboard/lesson/${lessonId}`);
    setMobileOpen(false);
  }

  const displayName = auth.firstName
    ? `${auth.firstName} ${auth.lastName || ""}`.trim()
    : (auth.email ?? "Student");

  const initials = auth.firstName
    ? `${auth.firstName[0]}${auth.lastName?.[0] ?? ""}`.toUpperCase()
    : "S";

  const SidebarContent = () => (
    <div className="h-full flex flex-col bg-white">
      {/* ── brand header ── */}
      <div className="px-4 py-4 border-b border-slate-100">
        <Link
          to="/dashboard"
          className="flex items-center gap-3 group"
          onClick={() => setMobileOpen(false)}
        >
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center font-black text-white text-lg shadow-sm">
            D
          </div>
          <div>
            <div className="text-sm font-bold text-slate-900 leading-tight">
              Digital Income
            </div>
            <div className="text-[11px] text-slate-400">Blueprint</div>
          </div>
        </Link>
      </div>

      {/* ── nav links ── */}
      <div className="px-3 py-3 border-b border-slate-100">
        {[
          {
            icon: <BarChart2 size={15} />,
            label: "Overview",
            path: "/dashboard",
          },
        ].map(({ icon, label, path }) => {
          const active = location.pathname === path;
          return (
            <Link
              key={path}
              to={path}
              onClick={() => setMobileOpen(false)}
              className={cn(
                "flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium transition-colors mb-1",
                active
                  ? "bg-teal-50 text-teal-700"
                  : "text-slate-600 hover:bg-slate-50 hover:text-slate-900",
              )}
            >
              <span className={active ? "text-teal-500" : "text-slate-400"}>
                {icon}
              </span>
              {label}
            </Link>
          );
        })}
      </div>

      {/* ── courses ── */}
      <div className="flex-1 overflow-y-auto px-3 py-4 space-y-3">
        <p className="px-1 text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">
          Your Courses
        </p>

        {coursesLoading && (
          <div className="space-y-2">
            {[1, 2].map((i) => (
              <div
                key={i}
                className="rounded-2xl border border-slate-200 p-4 animate-pulse"
              >
                <div className="h-3 bg-slate-100 rounded w-3/4 mb-2" />
                <div className="h-1.5 bg-slate-100 rounded w-full" />
              </div>
            ))}
          </div>
        )}

        {!coursesLoading && courses.length === 0 && (
          <div className="flex flex-col items-center justify-center py-8 text-center gap-2">
            <GraduationCap size={32} className="text-slate-200" />
            <p className="text-xs text-slate-400">No courses enrolled yet.</p>
          </div>
        )}

        {courses.map((course: any) => (
          <CourseAccordion
            key={course._id}
            course={course}
            currentLessonId={currentLessonId}
            onLesson={goToLesson}
          />
        ))}
      </div>

      {/* ── user footer ── */}
      <div className="border-t border-slate-100 p-3">
        <div className="flex items-center gap-3 px-2 py-2 rounded-xl mb-2">
          <div className="w-8 h-8 rounded-full bg-teal-500 flex items-center justify-center text-white text-xs font-bold shrink-0">
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-slate-800 truncate">
              {displayName}
            </p>
            <p className="text-[10px] text-slate-400 capitalize">
              {auth.role ?? "student"}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-1.5">
          <button
            onClick={() => {
              navigate("/dashboard/settings");
              setMobileOpen(false);
            }}
            className="flex items-center justify-center gap-1.5 px-2 py-2 rounded-xl text-xs font-medium text-slate-600 hover:bg-slate-100 transition-colors"
          >
            <Settings size={13} /> Settings
          </button>
          <button
            onClick={handleLogout}
            className="flex items-center justify-center gap-1.5 px-2 py-2 rounded-xl text-xs font-medium text-red-500 hover:bg-red-50 transition-colors"
          >
            <LogOut size={13} /> Log out
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="h-screen flex overflow-hidden bg-slate-50">
      {/* ── desktop sidebar ── */}
      <aside
        className={cn(
          "hidden md:flex flex-col flex-shrink-0 border-r border-slate-200 transition-all duration-300 overflow-hidden",
          sidebarOpen ? "w-72" : "w-0",
        )}
      >
        <SidebarContent />
      </aside>

      {/* ── mobile sidebar overlay ── */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          />
          <aside className="absolute left-0 top-0 bottom-0 w-[280px] z-10 shadow-2xl">
            <SidebarContent />
          </aside>
        </div>
      )}

      {/* ── main area ── */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* ── topbar ── */}
        <header className="bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between gap-3 shrink-0">
          <div className="flex items-center gap-3">
            {/* sidebar toggle */}
            <button
              onClick={() => {
                if (window.innerWidth < 768) setMobileOpen(true);
                else setSidebarOpen((v) => !v);
              }}
              className="p-2 rounded-xl hover:bg-slate-100 transition-colors text-slate-500"
              aria-label="Toggle sidebar"
            >
              <Menu size={18} />
            </button>

            {/* breadcrumb */}
            <div>
              <h1 className="text-base font-bold text-slate-900 leading-tight">
                {pageTitle}
              </h1>
              <p className="text-xs text-slate-400">
                Welcome back, {auth.firstName ?? "Student"} 👋
              </p>
            </div>
          </div>

          {/* right actions */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate("/dashboard")}
              className={cn(
                "hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold transition-colors",
                location.pathname === "/dashboard"
                  ? "bg-teal-50 text-teal-700"
                  : "text-slate-600 hover:bg-slate-100",
              )}
            >
              <BarChart2 size={14} /> Progress
            </button>
            

            {/* avatar */}
            <button
              onClick={() => navigate("/dashboard/settings")}
              className="w-8 h-8 rounded-full bg-teal-500 flex items-center justify-center text-white text-xs font-bold hover:bg-teal-600 transition-colors"
            >
              {initials}
            </button>
          </div>
        </header>

        {/* ── page content ── */}
        <main className="flex-1 overflow-auto">
          <div className="max-w-5xl mx-auto px-4 py-6 md:px-8 md:py-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default StudentLayout;
